import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Edit, Download, CheckCircle, AlertCircle } from 'lucide-react';
import CommonHeader from '../CommonHeader';

interface MeasurementData {
  id: string;
  customerName: string;
  date: string;
  status: 'complete' | 'partial' | 'pending';
  notes: string;
  measurements: {
    // Upper Body Measurements (in inches)
    neck: number;
    shoulder_width: number;
    chest: number;
    waist: number;
    bicep: number;
    forearm: number;
    wrist: number;
    sleeve_length: number;
    back_length: number;
    armhole: number;
    
    // Lower Body Measurements (in inches)
    hips: number;
    thigh: number;
    knee: number;
    calf: number;
    ankle: number;
    inseam: number;
    outseam: number;
    crotch: number;
    
    // Additional Measurements
    height: number;
    weight: number;
  };
}

const MeasurementDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  // Mock data - in real app, this would come from API
  const measurementData: MeasurementData = {
    id: id || '1',
    customerName: 'Gurpreet Singh',
    date: '2025-08-10',
    status: 'partial',
    notes: 'Initial measurements taken. Some measurements pending due to customer preference.',
    measurements: {
      // Upper Body Measurements (in inches)
      neck: 15.5,
      shoulder_width: 18.5,
      chest: 42,
      waist: 36,
      bicep: 14,
      forearm: 12,
      wrist: 7.5,
      sleeve_length: 25,
      back_length: 30,
      armhole: 20,
      
      // Lower Body Measurements (in inches)
      hips: 42,
      thigh: 24,
      knee: 16,
      calf: 15,
      ankle: 9,
      inseam: 32,
      outseam: 42,
      crotch: 28,
      
      // Additional Measurements
      height: 70, // inches
      weight: 160 // lbs
    }
  };

  const handleBack = () => {
    navigate('/customer/dashboard');
  };

  const handleEdit = () => {
    setIsEditing(!isEditing);
  };

  const handleDownload = () => {
    // In real app, this would generate a PDF
    console.log('Downloading measurement report...');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'complete': return 'bg-green-100 text-green-800';
      case 'partial': return 'bg-yellow-100 text-yellow-800';
      case 'pending': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'complete': return <CheckCircle className="w-4 h-4" />;
      case 'partial': return <AlertCircle className="w-4 h-4" />;
      case 'pending': return <AlertCircle className="w-4 h-4" />;
      default: return <AlertCircle className="w-4 h-4" />;
    }
  };

  const measurementsByCategory = measurementData.measurements.reduce((acc, measurement) => {
    if (!acc[measurement.category]) {
      acc[measurement.category] = [];
    }
    acc[measurement.category].push(measurement);
    return acc;
  }, {} as Record<string, MeasurementValue[]>);

  return (
    <div className="min-h-screen bg-gray-50">
      <CommonHeader
        title="Measurement Details"
        showBackButton={true}
        backTo="/customer/dashboard"
      >
        <div className="flex items-center space-x-4">
          <button
            onClick={handleEdit}
            className="flex items-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Edit className="w-4 h-4" />
            <span className="hidden sm:inline">{isEditing ? 'Save' : 'Edit'}</span>
          </button>
          <button
            onClick={handleDownload}
            className="flex items-center space-x-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Download</span>
          </button>
        </div>
      </CommonHeader>

      <main className="px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="space-y-6"
        >
          {/* Top Customer Info Bar */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{measurementData.customerName}</h3>
                  <p className="text-gray-600">Measurement taken on {new Date(measurementData.date).toLocaleDateString()}</p>
                </div>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(measurementData.status)}`}>
                  {getStatusIcon(measurementData.status)}
                  <span className="ml-2 capitalize">{measurementData.status}</span>
                </span>
              </div>
              <div className="flex items-center space-x-4">
                {measurementData.notes && (
                  <div className="text-sm text-gray-600 italic max-w-md">
                    "{measurementData.notes}"
                  </div>
                )}
                {/* Help Button */}
                <button
                  onClick={() => setShowHelp(true)}
                  className="flex items-center space-x-2 px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                >
                  <span className="text-sm">?</span>
                  <span className="hidden sm:inline text-sm">Help</span>
                </button>
              </div>
            </div>
          </div>

          {/* Tailoring Measurements */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Tailoring Measurements</h3>
            
            {/* Upper Body Measurements */}
            <div className="mb-8">
              <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <span className="mr-2">👔</span>
                Upper Body Measurements
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                <div className="bg-gray-50 p-3 rounded-lg text-center">
                  <div className="text-sm text-gray-600">Neck</div>
                  <div className="font-semibold text-lg">{measurementData.measurements.neck}"</div>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg text-center">
                  <div className="text-sm text-gray-600">Shoulder Width</div>
                  <div className="font-semibold text-lg">{measurementData.measurements.shoulder_width}"</div>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg text-center">
                  <div className="text-sm text-gray-600">Chest</div>
                  <div className="font-semibold text-lg">{measurementData.measurements.chest}"</div>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg text-center">
                  <div className="text-sm text-gray-600">Waist</div>
                  <div className="font-semibold text-lg">{measurementData.measurements.waist}"</div>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg text-center">
                  <div className="text-sm text-gray-600">Bicep</div>
                  <div className="font-semibold text-lg">{measurementData.measurements.bicep}"</div>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg text-center">
                  <div className="text-sm text-gray-600">Forearm</div>
                  <div className="font-semibold text-lg">{measurementData.measurements.forearm}"</div>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg text-center">
                  <div className="text-sm text-gray-600">Wrist</div>
                  <div className="font-semibold text-lg">{measurementData.measurements.wrist}"</div>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg text-center">
                  <div className="text-sm text-gray-600">Sleeve Length</div>
                  <div className="font-semibold text-lg">{measurementData.measurements.sleeve_length}"</div>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg text-center">
                  <div className="text-sm text-gray-600">Back Length</div>
                  <div className="font-semibold text-lg">{measurementData.measurements.back_length}"</div>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg text-center">
                  <div className="text-sm text-gray-600">Armhole</div>
                  <div className="font-semibold text-lg">{measurementData.measurements.armhole}"</div>
                </div>
              </div>
            </div>

            {/* Lower Body Measurements */}
            <div className="mb-8">
              <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <span className="mr-2">👖</span>
                Lower Body Measurements
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                <div className="bg-gray-50 p-3 rounded-lg text-center">
                  <div className="text-sm text-gray-600">Hips</div>
                  <div className="font-semibold text-lg">{measurementData.measurements.hips}"</div>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg text-center">
                  <div className="text-sm text-gray-600">Thigh</div>
                  <div className="font-semibold text-lg">{measurementData.measurements.thigh}"</div>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg text-center">
                  <div className="text-sm text-gray-600">Knee</div>
                  <div className="font-semibold text-lg">{measurementData.measurements.knee}"</div>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg text-center">
                  <div className="text-sm text-gray-600">Calf</div>
                  <div className="font-semibold text-lg">{measurementData.measurements.calf}"</div>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg text-center">
                  <div className="text-sm text-gray-600">Ankle</div>
                  <div className="font-semibold text-lg">{measurementData.measurements.ankle}"</div>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg text-center">
                  <div className="text-sm text-gray-600">Inseam</div>
                  <div className="font-semibold text-lg">{measurementData.measurements.inseam}"</div>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg text-center">
                  <div className="text-sm text-gray-600">Outseam</div>
                  <div className="font-semibold text-lg">{measurementData.measurements.outseam}"</div>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg text-center">
                  <div className="text-sm text-gray-600">Crotch</div>
                  <div className="font-semibold text-lg">{measurementData.measurements.crotch}"</div>
                </div>
              </div>
            </div>

            {/* Additional Measurements */}
            <div>
              <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <span className="mr-2">📏</span>
                Additional Measurements
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 p-3 rounded-lg text-center">
                  <div className="text-sm text-gray-600">Height</div>
                  <div className="font-semibold text-lg">{measurementData.measurements.height}" ({Math.floor(measurementData.measurements.height/12)}'{measurementData.measurements.height%12}")</div>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg text-center">
                  <div className="text-sm text-gray-600">Weight</div>
                  <div className="font-semibold text-lg">{measurementData.measurements.weight} lbs</div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Help Modal */}
        {showHelp && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 max-w-md mx-4">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-semibold text-gray-900">How to Use</h4>
                <button
                  onClick={() => setShowHelp(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
              <ul className="text-sm text-gray-700 space-y-2">
                <li>• View measurement details and progress</li>
                <li>• Edit measurements using the Edit button</li>
                <li>• Download measurement data as needed</li>
                <li>• Track completion status for each measurement</li>
              </ul>
              <button
                onClick={() => setShowHelp(false)}
                className="mt-4 w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Got it!
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default MeasurementDetails;
